import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, FolderTree, ChevronRight } from 'lucide-react';
import { categoriesApi } from '../api';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { Pagination, DEFAULT_PAGE_SIZE } from '../components/ui/Pagination';
import type { Category } from '../types';

interface CategoryFormData {
  name: string;
  parent: number | '';
  is_active: boolean;
}

const initialFormData: CategoryFormData = {
  name: '',
  parent: '',
  is_active: true,
};

export const Categories: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [categories, setCategories] = useState<Category[]>([]);
  const [treeCategories, setTreeCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const [listRes, treeRes] = await Promise.all([
        categoriesApi.list({ page_size: 100 }),
        categoriesApi.tree({ page, page_size: pageSize }),
      ]);

      const listData = Array.isArray(listRes.data) ? listRes.data : (listRes.data as any).results || [];
      const rawTree = treeRes.data as any;
      const treeData = Array.isArray(rawTree) ? rawTree : rawTree.results || [];
      const count = Array.isArray(rawTree) ? rawTree.length : rawTree.count || 0;

      setCategories(listData);
      setTreeCategories(treeData);
      setTotal(count);
    } catch (error: any) {
      // Oxirgi sahifadagi yagona kategoriya o'chirilsa DRF 404 qaytaradi —
      // bunday holda oldingi sahifaga qaytamiz.
      if (error.response?.status === 404 && page > 1) {
        setPage(page - 1);
        return;
      }
      console.error('Categories fetch error:', error);
      toast.error('Kategoriyalarni yuklashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const openAddModal = (parentId?: number) => {
    setEditingId(null);
    setFormData({
      ...initialFormData,
      parent: parentId || '',
    });
    setShowModal(true);
  };

  const openEditModal = async (id: number) => {
    try {
      const res = await categoriesApi.get(id);
      const c = res.data;
      setFormData({
        name: c.name,
        parent: c.parent || '',
        is_active: c.is_active,
      });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading category:', error);
      toast.error('Kategoriyani yuklashda xato');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        parent: formData.parent || null,
      };

      if (editingId) {
        await categoriesApi.update(editingId, payload);
        toast.success('Kategoriya yangilandi');
      } else {
        await categoriesApi.create(payload);
        toast.success('Kategoriya qo\'shildi');
      }
      closeModal();
      fetchCategories();
    } catch (error: any) {
      console.error('Save error:', error);
      const msg = error.response?.data?.detail || 'Saqlashda xato yuz berdi';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const confirmed = await confirm({
      title: "Kategoriyani o'chirish",
      description: `"${name}" kategoriyasini o'chirmoqchimisiz? Ichidagi barcha tovarlar kategoriyasiz qoladi.`,
      confirmLabel: "O'chirish",
      danger: true,
    });
    if (!confirmed) return;

    setDeleting(id);
    try {
      await categoriesApi.delete(id);
      toast.success("Kategoriya o'chirildi");
      fetchCategories();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("O'chirishda xato yuz berdi");
    } finally {
      setDeleting(null);
    }
  };

  const renderCategory = (category: Category, level: number = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedIds.has(category.id);

    return (
      <div key={category.id}>
        <div
          className="flex items-center justify-between py-2.5 pr-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          <div className="flex items-center gap-2 min-w-0">
            {hasChildren ? (
              <button
                onClick={() => toggleExpand(category.id)}
                className="p-1 hover:bg-gray-200 rounded shrink-0"
              >
                <ChevronRight
                  size={16}
                  className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
              </button>
            ) : (
              <span className="w-6 shrink-0" />
            )}
            <span className={`truncate ${category.is_active ? '' : 'text-gray-400'}`}>{category.name}</span>
            {category.products_count !== undefined && (
              <span className="text-xs text-gray-400 shrink-0">({category.products_count} ta)</span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => openAddModal(category.id)}
              className="p-1.5 text-success-600 hover:bg-success-50 rounded"
              title="Ichki kategoriya"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={() => openEditModal(category.id)}
              className="p-1.5 text-primary-800 hover:bg-primary-50 rounded"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => handleDelete(category.id, category.name)}
              disabled={deleting === category.id}
              className="p-1.5 text-danger-600 hover:bg-danger-50 rounded disabled:opacity-50"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {category.children!.map(child => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="page-bar flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kategoriyalar</h1>
        <button onClick={() => openAddModal()} className="btn btn-primary flex items-center gap-2">
          <Plus size={20} />
          <span>Yangi kategoriya</span>
        </button>
      </div>

      {/* Categories Tree */}
      <div className="card overflow-hidden !p-0">
        {loading ? (
          <SkeletonRows />
        ) : treeCategories.length === 0 ? (
          <EmptyState icon={FolderTree} title="Kategoriya topilmadi" />
        ) : (
          <div>
            {treeCategories.map(cat => renderCategory(cat))}
          </div>
        )}

        {/* Pagination — asosiy kategoriyalar bo'yicha */}
        {!loading && total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            count={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingId ? 'Kategoriyani tahrirlash' : 'Yangi kategoriya'}
        size="sm"
        footer={
          <>
            <button type="button" onClick={closeModal} className="btn btn-secondary">
              Bekor qilish
            </button>
            <button type="submit" form="category-form" disabled={saving} className="btn btn-primary">
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </>
        }
      >
        <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Nomi *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Ota kategoriya</label>
            <select
              className="input"
              value={formData.parent}
              onChange={(e) => setFormData({ ...formData, parent: e.target.value ? Number(e.target.value) : '' })}
            >
              <option value="">Asosiy kategoriya</option>
              {categories
                .filter(c => c.id !== editingId)
                .map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.full_path || cat.name}
                  </option>
                ))}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">Faol</span>
          </label>
        </form>
      </Modal>
    </div>
  );
};
